import React, { useCallback, useEffect, useState } from "react";
import { useSendForm } from "../../hooks/use-send-form";
import { Button } from "../ui/button/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog/dialog";
import { Input } from "../ui/input/input";
import { IMaskInput } from "react-imask";
import { TriangleAlert } from "lucide-react";
import { Label } from "../ui/label/label";
import { Checkbox } from "../ui/checkbox/checkbox";

import { useConsentStore } from "../../store/consent-store";
import { Separator } from "../ui/separator/separator";
import { InvisibleSmartCaptcha } from "@yandex/smart-captcha";

interface DialogFormProps {
  children: React.ReactNode;
  title: string;
  description: string;
  disclamer?: string
}

export const DialogForm = ({
  children,
  title,
  description,
  disclamer
}: DialogFormProps) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [isError, setError] = useState("");
  const [checked, setChecked] = useState(false);
  const [token, setToken] = useState('');
  const [resetCaptcha, setResetCaptcha] = useState(0);
  const [isVisibleCaptcha, setVisibleCaptcha] = useState(false);

  const { sendForm } = useSendForm();

  const consent = useConsentStore((state) => state.consent);
  const updateConsent = useConsentStore((state) => state.updateConsent);

  const resetForm = useCallback(() => {
    setName("");
    setPhone("");
    setChecked(false);
  }, []);

  const handleSmartCaptchaPointerEvents = () => {
    const body = document.body;
    if (body.classList.contains("!pointer-events-auto")) {
      body.classList.remove("!pointer-events-auto");
    } else {
      body.classList.add("!pointer-events-auto");
    }
  }

  const handleSendClick = (tokenParam?: string) => {
    const tokenCaptcha = tokenParam ?? token;
    if (tokenCaptcha !== '') {
      if (name && phone && checked) {
        sendForm({ name, phone });
        resetForm();
        setOpen(false);
        setVisibleCaptcha(false);
        setResetCaptcha((prev) => prev++);
      } else {
        setError("Пожалуйста, заполните все поля и политику конфеденциальности");
      }
    }
  };

  useEffect(() => {
    setError("");
  }, [name, phone]);

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[570px] rounded-[10px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-halvar leading-[100%] mb-3">
            {title}
          </DialogTitle>
          <Separator className="bg-plt-accent max-w-[80%] mx-auto mt-0" />
          <DialogDescription className="text-xl mt-5 font-gilroy">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Input
            className="bg-[#e2e2e2] h-[60px] text-xl"
            placeholder="Ваше имя"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <IMaskInput
            className="flex w-full rounded-md border border-input bg-[#e2e2e2] h-[60px] px-3 py-1 text-xl shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            onAccept={(value) => setPhone(value)}
            mask={"+7 (000) 000-00-00"}
            placeholder="+7 (___) ___-__-__"
          />
          {process.env.NODE_ENV === "production" && (
            <InvisibleSmartCaptcha
              key={resetCaptcha}
              sitekey={process.env.REACT_APP_SMARTCAPTCHA_KEY_CLIENT!}
              language="ru"
              shieldPosition="bottom-right"
              onSuccess={(token) => {
                setToken(token);
                handleSmartCaptchaPointerEvents();
                handleSendClick(token);
              }}
              visible={isVisibleCaptcha}
              onChallengeVisible={handleSmartCaptchaPointerEvents}
              onChallengeHidden={handleSmartCaptchaPointerEvents}
              onTokenExpired={() => setToken("")}
            />
          )}

          {isError && (
            <p className="text-red-500 text-xs flex items-center gap-2">
              <TriangleAlert size={16} />
              {isError}
            </p>
          )}

          <Button onClick={() => {
            setVisibleCaptcha(true)
            if (token !== '') {
              handleSendClick();
            }
          }} className="mt-5 h-[60px]">
            Заказать звонок
          </Button>

          <div className="flex gap-2 mt-5">
            <Checkbox
              className="rounded-none border-plt-black"
              id="terms"
              checked={checked}
              onCheckedChange={(checked) => setChecked(checked as boolean)}
            />
            <Label htmlFor="terms" className="flex font-normal text-xs">
              <span>
                Нажимая кнопку “Отправить”, вы даете согласие на обработку{" "}
                <a target="_blank" href="/docs/Politika-konf_futurist.docx">
                  персональных данных
                </a>
                .
              </span>
            </Label>
          </div>
          <div className="flex gap-2">
            <Checkbox
              className="rounded-none border-plt-black"
              id="cookies"
              checked={consent}
              onCheckedChange={(checked) => {
                updateConsent(checked as boolean);
              }}
            />
            <Label htmlFor="cookies" className="font-normal text-xs">
              Я ознакомлен (-на) с правилами использования куки
            </Label>
          </div>
          <a href="/docs/Politika-konf_futurist.docx" className="text-red-500">
            Политика конфиденциальности
          </a>
          {disclamer && <Dialog>
            <DialogTrigger>Подробнее об условиях</DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  Подробнее об условиях
                </DialogTitle>
                <DialogDescription className={'sr-only'}>
                  Подробнее об условиях
                </DialogDescription>
              </DialogHeader>
              <div>{disclamer}</div>
            </DialogContent>
          </Dialog>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
